import "./Tooltip.scss";

import clsx from "clsx";
import { type ReactNode, type Ref, useId } from "react";
import { ITooltip, Tooltip as ReactTooltip, TooltipRefProps } from "react-tooltip";

import { CircleInfoIcon } from "mds/icons";

export { type TooltipRefProps } from "react-tooltip";

export type TooltipAnchorProps = {
  "aria-describedby"?: string;
  "data-tooltip-delay-hide"?: number;
  "data-tooltip-delay-show"?: number;
  "data-tooltip-id"?: string;
  "data-tooltip-variant"?: "light";
};

export type TooltipKind = "default" | "card";

const KIND_CLASSES: Record<TooltipKind, string> = {
  default: "",
  card: "!bg-white !p-2 !text-black-tint-20 !shadow-modal",
};

export interface TooltipProps extends Omit<ITooltip, "id" | "content" | "children"> {
  content?: ReactNode;
  id?: string;
  kind?: TooltipKind;
  // TODO: we should remove the contentClassName prop so that all tooltips have the same styling.
  // This is currently only being used in AIAssistantDebugger, which is not user-facing.
  contentClassName?: string;
  wrapperClassName?: string;
  children?: ReactNode | ((anchorProps: TooltipAnchorProps) => ReactNode);
}

/**
 * Styled wrapper for react-tooltip.
 * Docs at https://react-tooltip.com/docs/getting-started
 *
 * Use {@link TooltipTrigger} when wrapping a non-focusable control (icon, text, etc.).
 *
 * To ensure that the tooltip will be read by screen readers, use a render-prop child when
 * wrapping a focusable control so anchor attrs (including aria-describedby) land on the
 * control itself:
 * <Tooltip content="…">{(tooltipAnchorProps) => <Button {...tooltipAnchorProps}>…</Button>}</Tooltip>
 *
 * Pass `anchorSelect` without children when the trigger is rendered outside React, such as
 * transformed Markdown HTML.
 */
export const Tooltip = ({
  ref,
  anchorSelect,
  contentClassName,
  wrapperClassName = "flex",
  children,
  content,
  id: providedId,
  kind = "default",
  opacity = 1,
  ...props
}: TooltipProps & { ref?: Ref<TooltipRefProps> }) => {
  const generatedId = useId();
  const id = providedId ?? `tooltip-${generatedId}`;

  if (!content) {
    return typeof children === "function"
      ? children({})
      : children || <CircleInfoIcon height="16" width="16" />;
  }

  const anchorProps: TooltipAnchorProps = {
    "aria-describedby": id,
    "data-tooltip-delay-hide": 0,
    "data-tooltip-delay-show": 0,
    "data-tooltip-id": id,
    "data-tooltip-variant": "light",
  };

  const hoverableElementContent =
    typeof children === "function"
      ? children(anchorProps)
      : children || <CircleInfoIcon height="16" width="16" />;

  const tooltip = (
    <ReactTooltip
      anchorSelect={anchorSelect}
      className={clsx("mds-tooltip-content", KIND_CLASSES[kind], contentClassName)}
      id={id}
      offset={8}
      opacity={opacity}
      disableStyleInjection
      noArrow
      {...props}
      globalCloseEvents={{
        ...props.globalCloseEvents,
        clickOutsideAnchor: true,
        escape: true,
      }}
      ref={ref}
    >
      {content}
    </ReactTooltip>
  );

  if (anchorSelect && !children) return tooltip;

  return (
    <div className={clsx("mds-tooltip", wrapperClassName)}>
      {typeof children === "function" ? (
        hoverableElementContent
      ) : (
        <span {...anchorProps} className="mds-tooltip-source">
          {hoverableElementContent}
        </span>
      )}
      {tooltip}
    </div>
  );
};

Tooltip.displayName = "Tooltip";
